import { useState, useEffect, useRef } from 'react';
import { Form, Row, Col, notification } from 'antd';
import { BaseCard } from '@app/components/common/BaseCard/BaseCard';
import { BaseInput } from '@app/components/common/inputs/BaseInput/BaseInput';
import { BaseSelect } from '@app/components/common/selects/BaseSelect/BaseSelect';
import { BaseButton } from '@app/components/common/BaseButton/BaseButton';
import { useHeader } from '@app/components/header/layouts/HeaderContext';
import { editLabService, showLabService, updateLabService } from '@app/api/labService.api';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface LabServiceFormProps {
  id?: string;
  pageTitle: string;
  // eslint-disable-next-line @typescript-eslint/ban-types
  setCurrentBreadcrumName: Function;
  editable?: boolean;
  type?: string;
}

const LabServiceForm: React.FC<LabServiceFormProps> = ({ editable, id, pageTitle, type, setCurrentBreadcrumName }) => {
  const [departmentOptions, setDepartmentOptions] = useState<{ label: string; value: string }[]>([]);
  const [labCategoryOptions, setLabCategoryOptions] = useState<{ label: string; value: string }[]>([]);
  const [typeOptions, setTypeOptions] = useState<{ label: string; value: string }[]>([]);
  const [statusOptions, setStatusOptions] = useState<{ label: string; value: string }[]>([]);
  const { addHeaderButton, removeHeaderButton } = useHeader();
  const navigate = useNavigate();
  const [status, setStatus] = useState<string | null>(null);
  const [form] = Form.useForm();
  const hasFetchedRef = useRef(false);
  const { t } = useTranslation();

  const [isLoading, setLoading] = useState(false);
  const labServiceId = id ? parseInt(id) : null;

  const setApiData = (res: any) => {
    form.setFieldsValue({
      department: res?.data?.lab_service_category?.department?.name,
      labCategory: res?.data?.lab_service_category?.name,
      labService: res?.data?.name,
      type: res?.data?.type,
      name: res?.data?.name,
      unitPrice: res?.data?.unit_price,
      uom: res?.data?.uom,
      status: res?.data?.status,
      description: res?.data?.description,
    });
    setStatus(res.data.status);
    setCurrentBreadcrumName(res?.data?.code || 'New');
  };

  const mapOptions = (options: any[] = []) =>
    options?.map((item) => ({
      label: item.label || item.name,
      value: item.value || item.name,
    }));

  const mapTypeOptions = (types: any) =>
    types
      ? Object.entries(types).map(([key, value]) => ({
          label: value as string,
          value: key,
        }))
      : [];

  const setAllOptions = (res: any) => {
    setDepartmentOptions(mapOptions(res.departments));
    setLabCategoryOptions(mapOptions(res.lab_service_categories));
    setTypeOptions(mapTypeOptions(res.types));
    setStatusOptions(res.statuses || []);
  };

  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    setLoading(true);

    if (labServiceId) {
      if (type === 'view') {
        showLabService(labServiceId)
          .then((res: any) => {
            setApiData(res);
            setLoading(false);
          })
          .catch((err: any) => {
            setLoading(false);
            notification.error({ message: 'Failed to view Lab Service', description: err.message });
            console.error(err);
          });
      } else if (type === 'edit') {
        editLabService(labServiceId)
          .then((res: any) => {
            setAllOptions(res);
            setApiData(res);
            setLoading(false);
          })
          .catch((err: any) => {
            setLoading(false);
            notification.error({ message: 'Failed to edit Lab Service', description: err.message });
            console.error(err);
          });
      }
    }
  }, []);

  useEffect(() => {
    if (editable) {
      addHeaderButton(
        'save-button',
        <BaseButton type="primary" htmlType="submit" loading={isLoading} onClick={() => form.submit()} size="small">
          {t('Save')}
        </BaseButton>,
      );

      addHeaderButton(
        'cancel-button',
        <BaseButton onClick={() => navigate('/lab-services')} style={{ marginRight: '8px' }} size="small">
          {t('Cancel')}
        </BaseButton>,
      );
    }

    if (!editable && id) {
      addHeaderButton(
        'edit-button',
        <BaseButton type="primary" onClick={() => navigate(`/edit-lab-service`, { state: { id } })} size="small">
          {t('Edit')}
        </BaseButton>,
      );
    }

    return () => {
      removeHeaderButton('save-button');
      removeHeaderButton('cancel-button');
      removeHeaderButton('edit-button');
    };
  }, [form, isLoading, editable, id, addHeaderButton, t, navigate, removeHeaderButton]);

  const handleSubmit = (values: any) => {
    if (editable && labServiceId) {
      const submissionData = {
        unit_price: values?.unitPrice || null,
        uom: values?.uom || null,
        status: values.status,
        description: values?.description || null,
      };

      if (type === 'edit') {
        updateLabService(labServiceId, submissionData)
          .then((res: any) => {
            setLoading(false);
            navigate(`/view-lab-service`, { state: { id: id } });
          })
          .catch((err: any) => {
            notification.error({ message: `${err.message}` });
            setLoading(false);
          });
      }
    }
    console.log('Form Submitted');
  };
  return (
    <BaseCard id="LabServiceForm" title={pageTitle} padding="1.25rem">
      <Form
        form={form}
        name="LabServiceForm"
        onFinish={handleSubmit}
        size="small"
        labelCol={{ lg: { span: 6 }, md: { span: 6 }, sm: { span: 24 } }}
        labelAlign="left"
        labelWrap
        wrapperCol={{ lg: { span: 16 }, md: { span: 18 }, sm: { span: 24 } }}
      >
        <Row gutter={16}>
          <Col xs={24} md={24} lg={12}>
            <Form.Item label="Department">
              <BaseInput readOnly value={form.getFieldValue('department') || ''} />
            </Form.Item>
          </Col>
          <Col xs={24} md={24} lg={12}>
            <Form.Item label="Lab Service Category">
              <BaseInput readOnly value={form.getFieldValue('labCategory') || ''} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} md={24} lg={12}>
            <Form.Item label="Lab Service">
              <BaseInput readOnly value={form.getFieldValue('labService') || ''} />
            </Form.Item>
          </Col>
          <Col xs={24} md={24} lg={12}>
            <Form.Item label="Type">
              <BaseInput readOnly value={form.getFieldValue('type') || ''} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} md={24} lg={12}>
            <Form.Item label="Name">
              <BaseInput readOnly value={form.getFieldValue('name') || ''} />
            </Form.Item>
          </Col>
          <Col xs={24} md={24} lg={12}>
            <Form.Item
              name="unitPrice"
              label="Unit Price"
              rules={[{ required: true, message: 'Unit Price is required' }]}
            >
              {!editable ? (
                <BaseInput readOnly value={form.getFieldValue('unitPrice') || ''} />
              ) : (
                <BaseInput placeholder="Enter Unit Price" readOnly={!editable} />
              )}
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} md={24} lg={12}>
            <Form.Item name="uom" label="UOM">
              {!editable ? (
                <BaseInput readOnly value={form.getFieldValue('uom') || ''} />
              ) : (
                <BaseInput placeholder="Enter Unit of Measure" readOnly={!editable} />
              )}
            </Form.Item>
          </Col>
          <Col xs={24} md={24} lg={12}>
            <Form.Item name="status" label="Status" rules={[{ required: true, message: 'Status is required' }]}>
              {!editable ? (
                <BaseInput readOnly value={form.getFieldValue('status') || ''} />
              ) : (
                <BaseSelect placeholder="Select Status" options={statusOptions} disabled={!editable} />
              )}
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} md={24} lg={12}>
            <Form.Item name="description" label="Description">
              {!editable ? (
                <BaseInput.TextArea readOnly value={form.getFieldValue('description') || ''} rows={4} />
              ) : (
                <BaseInput.TextArea placeholder="Enter Description" rows={4} readOnly={!editable} />
              )}
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </BaseCard>
  );
};

export default LabServiceForm;
